import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import z from "zod";

import { states } from "../../data/states";
import { useCityAndState } from "../../hooks/use-azure-maps-city-state";
import { DefaultEditableStaffMemberContactDetail, type StaffMemberContactDetails } from "../../hooks/use-user";
import { FormHelpers } from "../forms";
import { zipcodeSchema } from "../forms/form-helpers";
import { FormSection } from "../forms/form-section";
import { InputField } from "../forms/input-field";
import { PhoneNumberField } from "../forms/phone-number-field";
import { SelectField } from "../forms/select-field";

export const EditContactForm: React.FC<{
  editContactForm: UseFormReturn<EditContactFormValues>;

  isCurrentStep: boolean;
}> = ({
  editContactForm: {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors }
  },
  isCurrentStep
}) => {
  const zipCodeFieldValue = watch("zipcode") ?? "";
  const cityAndStateQuery = useCityAndState(zipCodeFieldValue ?? "", {
    enabled: FormHelpers.isValidZipcode(zipCodeFieldValue) && (!watch("city") || !watch("state"))
  });

  useEffect(() => {
    if (cityAndStateQuery?.data?.city && cityAndStateQuery?.data?.state) {
      setValue("city", cityAndStateQuery.data.city);
      setValue("state", cityAndStateQuery.data.state);
    }
  }, [cityAndStateQuery.data]);

  const onChangePhoneNumber = useCallback(() => {
    trigger("phoneNumbers");
  }, [trigger]);

  if (!isCurrentStep) {
    return null;
  }

  return (
    <form className="form step-body">
      <FormSection>
        <div className="col col-12 col-md-6">
          <InputField
            label="First Name"
            placeholder="First Name"
            isRequired
            error={errors.firstName}
            disableColClass
            {...register("firstName")}
          />
        </div>
        <div className="col col-12 col-md-6">
          <InputField
            label="Last Name"
            placeholder="Last Name"
            isRequired
            error={errors.lastName}
            disableColClass
            {...register("lastName")}
          />
        </div>
      </FormSection>

      <h3 className="card-subheader mt-3">Contact</h3>
      <FormSection>
        <div className="col col-12 col-md-6">
          <InputField
            label="Address 1"
            placeholder="Address 1"
            error={errors.line1}
            disableColClass
            {...register("line1")}
          />
        </div>
        <div className="col col-12 col-md-6">
          <InputField
            label="Address 2"
            placeholder="Address 2"
            error={errors.line2}
            disableColClass
            {...register("line2")}
          />
        </div>
        <div className="col col-12 col-md-4">
          <InputField label="ZIP" placeholder="ZIP" error={errors.zipcode} disableColClass {...register("zipcode")} />
        </div>
        <div className="col col-12 col-md-4">
          <InputField label="City" placeholder="City" error={errors.city} disableColClass {...register("city")} />
        </div>
        <div className="col col-12 col-md-4">
          <SelectField label="State" isRequired error={errors.state} disableColClass {...register("state")}>
            <option value="">Select a State</option>
            {states.map(({ abbreviation, name }) => (
              <option key={abbreviation} value={abbreviation}>
                {name}
              </option>
            ))}
          </SelectField>
        </div>
        <div className="col col-12 col-md-4">
          <PhoneNumberField
            label="Mobile Phone"
            placeholder="Mobile Phone"
            error={errors.mobilePhone ?? errors.phoneNumbers}
            validationState={errors.phoneNumbers && "error"}
            disableColClass
            {...register("mobilePhone", { onChange: onChangePhoneNumber })}
          />
        </div>
        <div className="col col-12 col-md-4">
          <PhoneNumberField
            label="Home Phone"
            placeholder="Home Phone"
            error={errors.homePhone}
            validationState={errors.phoneNumbers && "error"}
            disableColClass
            {...register("homePhone", { onChange: onChangePhoneNumber })}
          />
        </div>
        <div className="col col-12 col-md-4">
          <PhoneNumberField
            label="Other Phone"
            placeholder="Other Phone"
            error={errors.otherPhone}
            validationState={errors.phoneNumbers && "error"}
            disableColClass
            {...register("otherPhone", { onChange: onChangePhoneNumber })}
          />
        </div>
        <div className="col col-12">
          <InputField
            label="Personal Email"
            placeholder="Personal Email"
            isRequired
            error={errors.personalEmail}
            disableColClass
            {...register("personalEmail")}
          />
        </div>
      </FormSection>

      <h3 className="card-subheader mt-3">Emergency Contact</h3>
      <FormSection>
        <div className="col col-12 col-lg-6">
          <InputField
            label="Name"
            placeholder="Name"
            error={errors.emergencyContactName}
            disableColClass
            {...register("emergencyContactName")}
          />
        </div>
        <div className="col col-12 col-lg-6">
          <PhoneNumberField
            label="Phone"
            placeholder="Phone"
            error={errors.emergencyContactPhone}
            disableColClass
            {...register("emergencyContactPhone")}
          />
        </div>
      </FormSection>
    </form>
  );
};

const editContactFormSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, { message: "First name is required" })
      .max(20, { message: "must be 20 characters or less" }),
    lastName: z
      .string()
      .trim()
      .min(1, { message: "Last name is required" })
      .max(30, { message: "must be 30 characters or less" }),
    mobilePhone: z.string().trim().refine(FormHelpers.isValidOptionalPhoneNumber, { message: "Invalid phone number" }),
    homePhone: z.string().trim().refine(FormHelpers.isValidOptionalPhoneNumber, { message: "Invalid phone number" }),
    otherPhone: z.string().trim().refine(FormHelpers.isValidOptionalPhoneNumber, { message: "Invalid phone number" }),
    // fake field to hold "must have at least one phone number" error
    phoneNumbers: z.string().optional(),
    personalEmail: z.string().trim().email(),
    line1: z.string().trim(),
    line2: z.string().trim(),
    zipcode: zipcodeSchema.or(z.literal("")),
    city: z.string().trim(),
    state: z.string().min(1, { message: "Please select a state" }),
    emergencyContactName: z.string().trim(),
    emergencyContactPhone: z
      .string()
      .trim()
      .refine(FormHelpers.isValidOptionalPhoneNumber, { message: "Invalid phone number" })
  })
  .refine(({ mobilePhone, homePhone, otherPhone }) => mobilePhone || homePhone || otherPhone, {
    message: "At least one phone number is required",
    path: ["phoneNumbers"]
  });

type EditContactFormValues = z.infer<typeof editContactFormSchema>;

export const useEditContactForm = (staffMemberContactDetails: StaffMemberContactDetails | undefined) =>
  useForm<EditContactFormValues>({
    values: {
      ...DefaultEditableStaffMemberContactDetail,
      ...staffMemberContactDetails,
      state: staffMemberContactDetails?.state ?? ""
    },
    resolver: zodResolver(editContactFormSchema),
    mode: "onTouched"
  });
